import React from 'react'
import { useTheme } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import { useTranslation } from 'react-i18next'
import DropDown, { DropDownVariant } from '../Menus/DropDown'
import TeamTable from './TeamTable'
import Box from '@mui/material/Box'
import {
  PaginationResponse,
  UserAccountListing,
  FetchTeamForUserTeamStatusEnum,
} from '../../swagger'
import Divider from '@mui/material/Divider'
import ContainedButton, {
  ContainedButtonVariant,
} from '../Buttons/ContainedButton'
import SpaceBetweenSection from '../Elements/SpaceBetweenSection'
import { UserListingRoleAssignment } from '../../swagger/models/UserListingRoleAssignment'
import { styled } from '@mui/system'
import { Paper } from '@mui/material'
import { BlueHelpIcon } from '../Admin/Roles/Roles'
import useTeamReassignment from '../../hooks/useTeamReassignment'
import ReassignSupervisorModal from '../Modals/ReassignSupervisorModal'
import OutlinedButton, {
  OutlinedButtonVariant,
} from '../Buttons/OutlinedButton'
import { useAuth } from '../Routes/AuthProvider'
import { Can } from '@casl/react'

export enum SortOptions {
  lastNameASC = 'Sort By: Last Name (ASCENDING)',
  lastNameDESC = 'Sort By: Last Name (DESCENDING)',
  emailASC = 'Sort By: Email (ASCENDING)',
  emailDESC = 'Sort By: Email (DESCENDING)',
  roleASC = 'Sort By: Role (ASCENDING)',
  roleDESC = 'Sort By: Role (DESCENDING)',
}

/**
 * !!! Attempting to use component prop on Typography results in an error in the
 * form of TypeScript limitation regarding argument interface and overload
 * function signatures
 *
 * See known issues + workaround https://github.com/mui/material-ui/issues/15759#issuecomment-493994852
 */
const HeaderTypography = styled(Typography)(({ theme }) => ({
  color: theme.palette.textOrIcon.tableHeader,
  fontSize: '14px',
  lineHeight: '16px',
  [theme.breakpoints.down('sm')]: {
    margin: theme.spacing(3, 0),
  },
})) as typeof Typography

interface TeamSummaryTableProps {
  mySupport?: UserAccountListing[]
  peopleISupport?: UserAccountListing[]
  searchedTeam?: UserAccountListing[]
  updateUserSelection: (opts: {
    userKey: number
    name: string
    actorKey: number
    role?: string
  }) => void
  nameOfUserForTeam: string
  searchQuery?: string
  isViewingDownline: boolean
  selectedRoleAndUserName?: string
  handleRowsPerPage: (rowAmount: number) => void
  totalCount: number
  pageSize: number
  tableFooterPage: number
  handleSortDropDownSelection: (selection: string) => void
  sortOptions: {
    name: string
    id: SortOptions
  }[]
  teamStatusFilterOptions: {
    name: string
    id: FetchTeamForUserTeamStatusEnum
  }[]
  sortAccountsOrderAndLabel: string
  teamStatusOrderAndLabel: string
  handlePaginationChange?: (_pagination: PaginationResponse) => void
}

export const TeamSummaryTable: React.FC<TeamSummaryTableProps> = (props) => {
  const theme = useTheme()
  const { t } = useTranslation()
  const { permissionAbility } = useAuth()

  const {
    mySupport,
    peopleISupport,
    searchedTeam,
    updateUserSelection,
    searchQuery = '',
    nameOfUserForTeam,
    isViewingDownline,
    selectedRoleAndUserName,
    pageSize,
    handleRowsPerPage,
    totalCount,
    tableFooterPage,
    handleSortDropDownSelection,
    sortOptions,
    teamStatusFilterOptions,
    sortAccountsOrderAndLabel,
    teamStatusOrderAndLabel,
    handlePaginationChange,
  } = props

  // Use separate reassignment hooks for each table to maintain independent selection state
  const peopleISupportReassignment = useTeamReassignment({
    onReassignComplete: handlePaginationChange,
    currentPage: tableFooterPage,
    pageSize: pageSize,
    totalCount: totalCount,
  })

  const searchedTeamReassignment = useTeamReassignment({
    onReassignComplete: handlePaginationChange,
    currentPage: tableFooterPage,
    pageSize: pageSize,
    totalCount: totalCount,
  })

  const handleEmailTeam = () => {
    // Filter out actors with validTo date before now
    const activeActors = peopleISupport?.filter((actor) =>
      actor.roles.some((role) => role.validTo.getTime() >= new Date().getTime())
    )

    return (window.location.href = `mailto:${activeActors?.map(
      (it) => it.username
    )}`)
  }

  const textTypesForRoles = (
    role: UserListingRoleAssignment
  ): React.CSSProperties => {
    // Pending role
    if (role.validFrom.getTime() > new Date().getTime()) {
      return { fontStyle: 'italic' }
    } else if (role.validTo.getTime() < new Date().getTime()) {
      // "recent" role
      return { textDecoration: 'line-through' }
    }
    // Current role
    return {}
  }

  const peopleISupportProps = {
    ariaLabelledBy: 'peopleISupport',
  }

  const mySupportProps = {
    ariaLabelledBy: 'mySupport',
  }

  const searchedTeamProps = {
    ariaLabelledBy: 'searchResults',
  }

  return (
    <>
      <div>
        <Box sx={{ display: 'flex', gap: theme.spacing(2) }}>
          <DropDown
            formControlProps={{
              [theme.breakpoints.down('sm')]: {
                width: '100%',
                margin: theme.spacing(2, 0),
              },
            }}
            id="filterByTeamStatusType"
            menuOptions={teamStatusFilterOptions}
            value={teamStatusOrderAndLabel}
            handleSelection={handleSortDropDownSelection}
            variant={DropDownVariant.SortAndFilter}
          />
          <DropDown
            formControlProps={{
              [theme.breakpoints.down('sm')]: {
                width: '100%',
                margin: theme.spacing(2, 0),
              },
            }}
            id="sort"
            menuOptions={sortOptions}
            defaultValue={t('Team.SortOption.Sort', 'sort')}
            handleSelection={handleSortDropDownSelection}
            variant={DropDownVariant.SortAndFilter}
            value={sortAccountsOrderAndLabel}
          />
        </Box>
      </div>

      {isViewingDownline && (
        <Paper
          style={{
            marginTop: 24,
            height: 36,
            marginBottom: theme.spacing(3),
          }}
          component="section"
          aria-labelledby="legend-label"
        >
          <Typography
            variant="body2"
            id="legend-label"
            component="h2"
            display="inline"
          >
            <BlueHelpIcon
              style={{
                color: theme.palette.customBackground.onPrimary,
                margin: theme.spacing(1),
                marginInlineStart: `${theme.spacing(1.5)}`,
              }}
            />
            {t('UserAccounts.Table.Legend.Title', 'KEY:')}
            <Box
              style={{
                display: 'inline',
                color: 'primary.main',
                fontFamily: 'plantin, sans-serif',
              }}
            >
              <Typography
                component="span"
                style={{
                  fontStyle: 'italic',
                  marginLeft: theme.spacing(1.5),
                }}
              >
                {t('UserAccounts.Table.Legend.PendingRole', 'Pending Role, ')}
              </Typography>
              <Typography component="span">
                {t('UserAccounts.Table.Legend.CurrentRole', 'Current Role, ')}
              </Typography>
              <Typography
                component="span"
                style={{
                  textDecoration: 'line-through',
                }}
              >
                {t('UserAccounts.Table.Legend.RecentRole', 'Recent Role')}
              </Typography>
            </Box>
          </Typography>
        </Paper>
      )}

      {searchedTeam && (
        <>
          <Box mt={2}>
            <Can I="bulkReassignRoles" on="User" ability={permissionAbility}>
              <SpaceBetweenSection
                isCentered
                left={
                  <HeaderTypography
                    id="searchResults"
                    variant="button"
                    component="p"
                  >
                    {t(
                      'TeamSummaryTable.Header.SearchResults',
                      `Search Results for "{{searchQuery}}"`,
                      { searchQuery }
                    )}
                  </HeaderTypography>
                }
                right={
                  teamStatusOrderAndLabel !==
                    FetchTeamForUserTeamStatusEnum.Inactive && (
                    <Box display="flex" gap={1} alignItems="center">
                      {/* Add Reassign Supervisor button */}
                      <OutlinedButton
                        id="reassignTeamMembers"
                        variant={OutlinedButtonVariant.ReassignTeamMembers}
                        onClick={
                          searchedTeamReassignment.handleOpenReassignModal
                        }
                        css={{
                          [theme.breakpoints.down('sm')]: {
                            margin: theme.spacing(1, 0, 2.5, 0),
                          },
                        }}
                        disabled={
                          searchedTeamReassignment.selectedRows.length === 0
                        }
                      />
                    </Box>
                  )
                }
              />
            </Can>
            <TeamTable
              handleRowsPerPage={handleRowsPerPage}
              totalCount={totalCount}
              pageSize={pageSize}
              tableFooterPage={tableFooterPage}
              support={searchedTeam}
              updateUserSelection={updateUserSelection}
              textTypesForRoles={textTypesForRoles}
              checkboxSelection={true}
              onSelectionModelChange={
                searchedTeamReassignment.handleSelectionChange
              }
              selectionModel={searchedTeamReassignment.selectedRows}
              {...searchedTeamProps}
            />
          </Box>
          <Divider />
        </>
      )}

      {mySupport && (
        <Box mt={2}>
          <HeaderTypography id="mySupport" variant="button" component="p">
            {!!nameOfUserForTeam
              ? t(
                  'Team.Header.SupportOfUser',
                  "{{selectedRoleAndUserName}}'s Support",
                  {
                    selectedRoleAndUserName,
                  }
                )
              : t('Team.Header.MySupport', 'My Support')}
          </HeaderTypography>
          <TeamTable
            tableFooterPage={tableFooterPage}
            handleRowsPerPage={handleRowsPerPage}
            totalCount={totalCount}
            pageSize={pageSize}
            support={mySupport}
            isClickable={false} // This row shouldn't be clickable. It won't take you anywhere.
            textTypesForRoles={textTypesForRoles}
            {...mySupportProps}
          />
        </Box>
      )}

      {peopleISupport && (
        <>
          <SpaceBetweenSection
            isCentered
            left={
              <HeaderTypography
                id="peopleISupport"
                variant="button"
                component="p"
              >
                {!!nameOfUserForTeam
                  ? t(
                      'Team.Header.PeopleAUserSupports',
                      'People {{selectedRoleAndUserName}} Supports',
                      {
                        selectedRoleAndUserName,
                      }
                    )
                  : t('Team.Header.PeopleISupport', 'People I Support')}
              </HeaderTypography>
            }
            right={
              teamStatusOrderAndLabel !==
                FetchTeamForUserTeamStatusEnum.Inactive && (
                <Box display="flex" gap={1} alignItems="center">
                  <Can
                    I="bulkReassignRoles"
                    on="User"
                    ability={permissionAbility}
                  >
                    <OutlinedButton
                      id="reassignTeamMembers"
                      variant={OutlinedButtonVariant.ReassignTeamMembers}
                      onClick={
                        peopleISupportReassignment.handleOpenReassignModal
                      }
                      css={{
                        [theme.breakpoints.down('sm')]: {
                          margin: theme.spacing(1, 0, 2.5, 0),
                        },
                      }}
                      disabled={
                        peopleISupportReassignment.selectedRows.length === 0
                      }
                    />
                  </Can>

                  {peopleISupport.length > 0 && (
                    <ContainedButton
                      fullWidth
                      id="emailTeamButton"
                      variant={ContainedButtonVariant.EmailPeopleISupport}
                      onClick={handleEmailTeam}
                      css={{
                        [theme.breakpoints.down('sm')]: {
                          margin: theme.spacing(1, 0, 2.5, 0),
                        },
                      }}
                    />
                  )}
                </Box>
              )
            }
          />
          <TeamTable
            handleRowsPerPage={handleRowsPerPage}
            isPaginated
            pageSize={pageSize}
            tableFooterPage={tableFooterPage}
            totalCount={totalCount}
            support={peopleISupport}
            textTypesForRoles={textTypesForRoles}
            updateUserSelection={updateUserSelection}
            handlePaginationChange={handlePaginationChange}
            onSelectionModelChange={
              peopleISupportReassignment.handleSelectionChange
            }
            selectionModel={peopleISupportReassignment.selectedRows}
            checkboxSelection={permissionAbility.can(
              'bulkReassignRoles',
              'User'
            )}
            {...peopleISupportProps}
          />
        </>
      )}

      {searchedTeam && (
        <ReassignSupervisorModal
          open={searchedTeamReassignment.isReassignModalOpen}
          onClose={searchedTeamReassignment.handleCloseReassignModal}
          onReassign={searchedTeamReassignment.handleReassignSupervisor}
          selectedCount={searchedTeamReassignment.selectedRows.length}
          selectedSupervisor={searchedTeamReassignment.selectedSupervisor}
          setSelectedSupervisor={searchedTeamReassignment.setSelectedSupervisor}
          supervisorOptions={searchedTeamReassignment.supervisorOptions}
          isLoading={searchedTeamReassignment.isLoadingSupervisors}
          isReassigning={searchedTeamReassignment.isReassigning}
          onInputChange={searchedTeamReassignment.setSupervisorSearchInput}
        />
      )}

      {peopleISupport && (
        <ReassignSupervisorModal
          open={peopleISupportReassignment.isReassignModalOpen}
          onClose={peopleISupportReassignment.handleCloseReassignModal}
          onReassign={peopleISupportReassignment.handleReassignSupervisor}
          selectedCount={peopleISupportReassignment.selectedRows.length}
          selectedSupervisor={peopleISupportReassignment.selectedSupervisor}
          setSelectedSupervisor={
            peopleISupportReassignment.setSelectedSupervisor
          }
          supervisorOptions={peopleISupportReassignment.supervisorOptions}
          isLoading={peopleISupportReassignment.isLoadingSupervisors}
          isReassigning={peopleISupportReassignment.isReassigning}
          onInputChange={peopleISupportReassignment.setSupervisorSearchInput}
        />
      )}
    </>
  )
}

export default TeamSummaryTable
